import { parseColor, type Color } from "@ark-ui/react/color-picker";
import { hubTagColorIds } from "@bafe/validation";

export type HubTagColorId = (typeof hubTagColorIds)[number];

export const HUB_TAG_COLOR_IDS: readonly HubTagColorId[] = hubTagColorIds;

/** Solid fills for swatches (keep in sync with `--hub-tone-solid` in platform.css). */
export const HUB_TONE_SOLID_HEX: Record<HubTagColorId, string> = {
	zinc: "#52525b",
	slate: "#475569",
	stone: "#57534e",
	sky: "#2f6fed",
	cyan: "#0e7490",
	indigo: "#4f46e5",
	navy: "#1e40af",
	violet: "#6d4ad4",
	fuchsia: "#a21caf",
	rose: "#e23b3b",
	coral: "#c2410c",
	amber: "#c47b12",
	orange: "#d4651a",
	olive: "#4d7c0f",
	lime: "#65a30d",
	mint: "#1c8a4a",
	teal: "#0f7c86",
};

const HUB_HEX_TO_TONE = new Map(
	Object.entries(HUB_TONE_SOLID_HEX).map(([id, hex]) => [hex.toLowerCase(), id as HubTagColorId]),
);

/** Shared soft tones for tag chips and assignee pills (`hub-tone-*` in platform.css). */
export const HUB_TONE_CLASS_PREFIX = "hub-tone-";

export function hubToneSolidHex(colorId: HubTagColorId): string {
	return HUB_TONE_SOLID_HEX[colorId];
}

function normalizePickerHex(hex: string): string {
	const lower = hex.trim().toLowerCase();
	if (lower.length === 9 && lower.startsWith("#")) return lower.slice(0, 7);
	return lower;
}

export function hubToneIdFromHex(input: string): HubTagColorId | undefined {
	const direct = HUB_HEX_TO_TONE.get(normalizePickerHex(input));
	if (direct) return direct;
	try {
		return HUB_HEX_TO_TONE.get(normalizePickerHex(parseColor(input).toString("hex")));
	} catch {
		return undefined;
	}
}

/** Map a picker color to the nearest hub preset (exact hex first). */
export function hubToneIdFromPickerValue(value: Color | string): HubTagColorId | undefined {
	const color = typeof value === "string" ? parseColor(value) : value;
	const exact = hubToneIdFromHex(color.toString("hex"));
	if (exact) return exact;

	let best: HubTagColorId | undefined;
	let bestDistance = Number.POSITIVE_INFINITY;
	for (const id of HUB_TAG_COLOR_IDS) {
		const preset = parseColor(HUB_TONE_SOLID_HEX[id]);
		const dr = color.getChannelValue("red") - preset.getChannelValue("red");
		const dg = color.getChannelValue("green") - preset.getChannelValue("green");
		const db = color.getChannelValue("blue") - preset.getChannelValue("blue");
		const distance = dr * dr + dg * dg + db * db;
		if (distance < bestDistance) {
			bestDistance = distance;
			best = id;
		}
	}
	return best;
}

function stableIndex(key: string, size: number): number {
	const norm = key.trim().toLowerCase();
	let hash = 0;
	for (let i = 0; i < norm.length; i += 1) {
		hash = (hash * 31 + norm.charCodeAt(i)) >>> 0;
	}
	return size ? hash % size : 0;
}

export function defaultTagColorId(tagName: string): HubTagColorId {
	return HUB_TAG_COLOR_IDS[stableIndex(tagName, HUB_TAG_COLOR_IDS.length)] ?? "zinc";
}

export function assigneeColorId(assigneeKey: string): HubTagColorId {
	const offset = stableIndex(assigneeKey, HUB_TAG_COLOR_IDS.length);
	const shifted = (offset + 3) % HUB_TAG_COLOR_IDS.length;
	return HUB_TAG_COLOR_IDS[shifted] ?? "sky";
}

export function resolveAssigneeColorId(
	assigneeKey: string,
	colorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>,
): HubTagColorId {
	const key = assigneeKey.trim().toLowerCase();
	if (!key) return "sky";
	const stored = colorByLowerName.get(key);
	if (stored && isHubTagColorId(stored)) return stored;
	const fromHex = stored ? hubToneIdFromHex(stored) : undefined;
	if (fromHex) return fromHex;
	return assigneeColorId(assigneeKey);
}

export function isHubTagColorId(value: string | undefined): value is HubTagColorId {
	return Boolean(value && HUB_TAG_COLOR_IDS.includes(value as HubTagColorId));
}

export function isHubStoredColor(value: string | undefined): boolean {
	if (!value) return false;
	if (isHubTagColorId(value)) return true;
	return /^#[0-9a-f]{6}$/i.test(value.trim());
}

/** Always a #rrggbb for the picker and CSS variables. */
export function hubColorHex(color: string | undefined, fallbackKey: string): string {
	if (color && isHubTagColorId(color)) return HUB_TONE_SOLID_HEX[color];
	if (color) {
		try {
			return normalizePickerHex(parseColor(color).toString("hex"));
		} catch {
			/* fall through */
		}
	}
	return HUB_TONE_SOLID_HEX[defaultTagColorId(fallbackKey)];
}

export function hubToneClass(colorId: HubTagColorId | string | undefined, fallbackKey: string): string {
	if (colorId && isHubTagColorId(colorId)) return `${HUB_TONE_CLASS_PREFIX}${colorId}`;
	const hex = colorId ? hubToneIdFromHex(colorId) : undefined;
	if (hex) return `${HUB_TONE_CLASS_PREFIX}${hex}`;
	if (colorId && /^#[0-9a-f]{6}$/i.test(colorId.trim())) return "hub-tone-custom";
	return `${HUB_TONE_CLASS_PREFIX}${defaultTagColorId(fallbackKey)}`;
}

export function hubToneStyle(
	colorId: HubTagColorId | string | undefined,
	fallbackKey: string,
): Record<string, string> | undefined {
	if (hubToneClass(colorId, fallbackKey) !== "hub-tone-custom") return undefined;
	return { "--hub-tone-solid": hubColorHex(colorId, fallbackKey) };
}

export function resolveTagColorId(
	tagName: string,
	colorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>,
): HubTagColorId {
	const stored = colorByLowerName.get(tagName.trim().toLowerCase());
	if (stored && isHubTagColorId(stored)) return stored;
	const fromHex = stored ? hubToneIdFromHex(stored) : undefined;
	if (fromHex) return fromHex;
	return defaultTagColorId(tagName);
}

export function resolveTagColorValue(
	tagName: string,
	colorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>,
): string {
	const stored = colorByLowerName.get(tagName.trim().toLowerCase());
	if (stored && isHubStoredColor(stored)) return isHubTagColorId(stored) ? stored : stored.toLowerCase();
	return defaultTagColorId(tagName);
}

export function resolveAssigneeColorValue(
	assigneeKey: string,
	colorByLowerName: ReadonlyMap<string, HubTagColorId | string | undefined>,
): string {
	const key = assigneeKey.trim().toLowerCase();
	if (!key) return "sky";
	const stored = colorByLowerName.get(key);
	if (stored && isHubStoredColor(stored)) return isHubTagColorId(stored) ? stored : stored.toLowerCase();
	return assigneeColorId(assigneeKey);
}
